// src/pages/Admin/Cities/AdminClubs.jsx
import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/utils/axios";

import cls from "./AdminClubs.module.scss";

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString();
  } catch {
    return "—";
  }
};

const AdminClubs = () => {
  const { cityId, streetId } = useParams();

  const [clubs, setClubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [streetMeta, setStreetMeta] = useState(null);

  const fallbackImg = "https://dummyimage.com/120x80/1a1a1a/ffffff&text=Club";

  const getImg = (club) => {
    if (!club) return fallbackImg;
    return club.avatarThumb?.url || club.avatar?.url || fallbackImg;
  };

  const fetchStreetMeta = useCallback(async () => {
    if (!cityId || !streetId) return;
    try {
      const { data } = await api.get(
        `/v1/admin/cities/${cityId}/streets/${streetId}`
      );
      setStreetMeta(data || null);
    } catch (e) {
      console.error("Failed to load street meta", e);
    }
  }, [cityId, streetId]);

  const fetchClubs = useCallback(async () => {
    if (!cityId || !streetId) return;
    try {
      setLoading(true);
      setErr("");
      const { data } = await api.get(
        `/v1/admin/cities/${cityId}/streets/${streetId}/clubs`
      );
      setClubs(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("Failed to load clubs", e);
      setErr(
        e?.response?.data?.message || e?.message || "Could not load clubs."
      );
      setClubs([]);
    } finally {
      setLoading(false);
    }
  }, [cityId, streetId]);

  useEffect(() => {
    fetchStreetMeta();
    fetchClubs();
  }, [fetchStreetMeta, fetchClubs]);

  const streetName = streetMeta?.streetName || "Back to Streets";

  return (
    <div className={cls.wrap}>
      <header className={cls.header}>
        <div>
          {/* ✅ Back to streets */}
          {cityId && (
            <Link
              className={cls.backBtn}
              to={`/admin/cities/single/${cityId}/streets`}
            >
              ← {streetName}
            </Link>
          )}

          <p className={cls.eyebrow}>Admin · Clubs</p>
          <h1 className="nu-title is-mr" data-glow="1">
            {streetMeta?.streetName
              ? `Clubs · ${streetMeta.streetName}`
              : "Clubs"}
          </h1>
        </div>

        <div className={cls.actions}>
          <button
            className="nu-btn nu-btn--ghost"
            type="button"
            onClick={fetchClubs}
          >
            Refresh
          </button>

          {cityId && streetId && (
            <Link
              className="nu-btn nu-btn--primary"
              to={`/admin/cities/single/${cityId}/streets/${streetId}/clubs/create`}
            >
              + Create Club
            </Link>
          )}
        </div>
      </header>

      {err && <div className={cls.error}>⚠ {err}</div>}

      <div className={cls.grid} aria-busy={loading ? "true" : "false"}>
        {loading ? (
          [...Array(6)].map((_, i) => (
            <div key={`sk-${i}`} className={`${cls.card} ${cls.skeleton}`}>
              <div className={cls.skelThumb} />
              <div className={cls.skelLine} />
              <div className={cls.skelLineWide} />
            </div>
          ))
        ) : clubs.length === 0 ? (
          <div className={cls.empty}>
            No clubs yet. Create the first club for this street.
          </div>
        ) : (
          clubs.map((club) => (
            <article key={club._id} className={cls.card}>
              <div className={cls.thumbWrap}>
                <img
                  src={getImg(club)}
                  alt={club.clubName || "Club avatar"}
                  className={cls.thumb}
                />
                <div className={cls.status} data-status={club.status}>
                  {club.status || "unknown"}
                </div>
              </div>

              <div className={cls.meta}>
                <div className={cls.titleRow}>
                  <h3>{club.clubName || "Untitled Club"}</h3>
                  <span className={cls.lang}>
                    {club.clubLanguage?.toUpperCase?.() || "EN"}
                  </span>
                </div>

                <p className={cls.sub}>{club.clubVibe || "No vibe set."}</p>

                <p className={cls.updated}>
                  Updated {formatDate(club.updatedAt)}
                </p>
              </div>

              <div className={cls.actionsRow}>
                <Link
                  className="nu-btn nu-btn--ghost"
                  to={`/admin/cities/single/${cityId}/streets/${streetId}/clubs/${club._id}`}
                >
                  View
                </Link>
              </div>
            </article>
          ))
        )}
      </div>
    </div>
  );
};

export default AdminClubs;
